import React, { useState, useEffect, useRef } from 'react'
import Video from '/react4xp/components/Video'
import { createRoot } from 'react-dom/client'

interface Slide {
   name?: string
   position?: string
   company?: string
   quote?: string
   image?: {
      src?: string
      alt?: string
   }
   video?: {
      src?: string
      type?: 'youtube' | 'default'
   }
}

interface Props {
   title: string
   slides: Slide[]
}

const closeButton = (
   <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="24" height="24" rx="12" fill="white" />
      <path d="M16.2945 7.70465L7.70361 16.2956" stroke="#002855" strokeWidth="1.74545" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M16.2945 16.2956L7.70361 7.70465" stroke="#002855" strokeWidth="1.74545" strokeLinecap="round" strokeLinejoin="round" />
   </svg>
)

const playButton = (
   <svg width="56" height="64" viewBox="0 0 56 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M54 28.5052C56.6667 30.0584 56.6667 33.9416 54 35.4948L6 63.4533C3.33333 65.0066 -2.75666e-06 63.065 -2.62087e-06 59.9585L-1.76659e-07 4.04148C-4.08695e-08 0.934982 3.33333 -1.00658 6 0.546668L54 28.5052Z" fill="#DBE5DB" />
   </svg>
)

const DRAG_THRESHOLD = 5

export default function Slide(props: Props) {
   const { title, slides } = props

   const marginRef = useRef<HTMLDivElement>(null)
   const trackRef = useRef<HTMLDivElement>(null)
   const modalContainerRef = useRef<HTMLDivElement | null>(null)
   const modalContentRef = useRef<HTMLDivElement | null>(null)
   const rootRef = useRef<any>(null)

   const [containerMargin, setContainerMargin] = useState<number>(0)
   const [trackPercentage, setTrackPercentage] = useState<number>(0)
   const [trackProgress, setTrackProgress] = useState<number>(0)
   const [modalOpen, setModalOpen] = useState<boolean>(false)
   const [currentModalIndex, setCurrentModalIndex] = useState<number>(0)

   const [isDragging, setIsDragging] = useState<boolean>(false)
   const [startX, setStartX] = useState<number>(0)
   const [scrollLeft, setScrollLeft] = useState<number>(0)
   const dragDistanceRef = useRef<number>(0)

   useEffect(() => {
      const calculateMargin = () => {
         if (marginRef.current) {
            const elementLeft = marginRef.current.getBoundingClientRect().left
            setContainerMargin(elementLeft)
         }
      }

      const calculatePercentage = () => {
         if (trackRef.current && marginRef.current) {
            const trackWidth = trackRef.current.scrollWidth - (containerMargin * 2)
            const containerWidth = marginRef.current.scrollWidth

            if (trackWidth > containerWidth) {
               const percentage = containerWidth / trackWidth
               setTrackPercentage(Math.min(percentage, 1))
            } else {
               setTrackPercentage(1)
            }
         }
      }

      const calculateProgress = () => {
         if (trackRef.current) {
            const trackWidth = trackRef.current.scrollWidth - (containerMargin * 2)
            const scrollLeft = trackRef.current.scrollLeft

            setTrackProgress(scrollLeft / trackWidth)
         }
      }

      const handleResize = () => {
         calculateMargin()
         calculatePercentage()
      }

      const handleScroll = () => {
         calculateProgress()
      }

      calculateMargin()
      calculatePercentage()

      window.addEventListener('resize', handleResize)
      trackRef.current?.addEventListener('scroll', handleScroll)

      return () => {
         window.removeEventListener('resize', handleResize)
         trackRef.current?.removeEventListener('scroll', handleScroll)
      }
   }, [containerMargin])

   useEffect(() => {
      const handleEscape = (event: KeyboardEvent) => {
         if (event.key === 'Escape') {
            closeModal()
         }
      }

      const handleClickOutside = (event: MouseEvent) => {
         if (modalOpen && modalContainerRef.current && modalContentRef.current && !modalContentRef.current.contains(event.target as Node)) {
            closeModal()
         }
      }

      if (modalOpen) {
         document.addEventListener('keydown', handleEscape)
         document.addEventListener('mousedown', handleClickOutside)
      }

      return () => {
         document.removeEventListener('keydown', handleEscape)
         document.removeEventListener('mousedown', handleClickOutside)
      }
   }, [modalOpen])

   useEffect(() => {
      if (modalOpen) {
         if (!modalContainerRef.current) {
            modalContainerRef.current = document.createElement('div')
            modalContainerRef.current.className = 'modalWrapper'
            document.body.appendChild(modalContainerRef.current)
            rootRef.current = createRoot(modalContainerRef.current)
         }

         const formattedVideoProps = slides[currentModalIndex].video
            ? {
               url: slides[currentModalIndex].video?.src ?? '',
               videoType: slides[currentModalIndex].video?.type ?? 'default',
               poster: slides[currentModalIndex].image?.src ?? '',
            }
            : null

         rootRef.current.render(
            <div className="slide-component__modal" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', overflow: 'hidden', zIndex: 1000 }}>
               <div ref={modalContentRef} className="modal-wrapper">
                  <button className="close-button" onClick={closeModal}>
                     {closeButton}
                  </button>
                  <figure>
                     <Video video={formattedVideoProps} />
                  </figure>
               </div>
            </div>
         )

         document.body.style.overflow = 'hidden'
      }

      return () => {
         if (!modalOpen && rootRef.current) {
            rootRef.current.unmount()
            modalContainerRef.current?.remove()
         }
      }
   }, [modalOpen, currentModalIndex, slides])

   const openModal = (index: number) => {
      setCurrentModalIndex(index)
      setModalOpen(true)
   }

   const closeModal = () => {
      setModalOpen(false)

      if (rootRef.current && modalContainerRef.current) {
         rootRef.current.unmount()
         modalContainerRef.current.remove()
         modalContainerRef.current = null
         document.body.style.overflow = ''
      }
   }

   const handleClick = (index: number, e: React.MouseEvent) => {
      if (dragDistanceRef.current > DRAG_THRESHOLD) {
         e.preventDefault()
         e.stopPropagation()
         return
      }

      openModal(index)
   }

   const startDragging = (e: React.MouseEvent | React.TouchEvent) => {
      setIsDragging(true)
      dragDistanceRef.current = 0
      const startPos = 'touches' in e ? e.touches[0].pageX : e.pageX
      setStartX(startPos - trackRef.current!.offsetLeft)
      setScrollLeft(trackRef.current!.scrollLeft)
   }

   const stopDragging = () => {
      setIsDragging(false)
   }

   const handleDragging = (e: React.MouseEvent | React.TouchEvent) => {
      if (!isDragging) return

      const moveX = 'touches' in e ? e.touches[0].pageX : e.pageX
      const deltaX = moveX - startX
      trackRef.current!.scrollLeft = scrollLeft - deltaX

      dragDistanceRef.current += Math.abs(deltaX)
   }

   return (
      <div className="slide-component">
         <div className="grid-container">
            <div ref={marginRef} className="grid-row">
               <div className="c-md-10">
                  {title && <h2 className="slide-component__title">{title}</h2>}
               </div>
            </div>
         </div>

         <div
            ref={trackRef}
            className="slide-component__track"
            style={{ paddingInline: `${containerMargin}px` }}
            onMouseDown={startDragging}
            onTouchStart={startDragging}
            onMouseMove={handleDragging}
            onTouchMove={handleDragging}
            onMouseUp={stopDragging}
            onMouseLeave={stopDragging}
            onTouchEnd={stopDragging}
         >
            {slides.map((slide, index) => {
               return (
                  <div key={index} className="slide-component__item">
                     <div className="slide-component__wrapper">
                        <figure
									onClick={slide.video ? (event) => handleClick(index, event) : undefined}
									style={{ cursor: `${slide.video ? 'pointer' : ''}` }}
								>
									
									{slide.image ? (
										<img draggable={false} src={slide.image?.src} alt={slide.image?.alt} loading='lazy' />
									) : (
										<div className='slide-component__placeholder'></div>
									)}

                           {slide.video && playButton}

                        </figure>

                        {(slide.name || slide.position || slide.company || slide.quote) && (
                           <div className="slide-component__content">
                              {(slide.name || slide.position || slide.company) && (
                                 <div className="info">
                                    {slide.name && <div className="name">{slide.name}</div>}
                                    {slide.position && <div className="position">{slide.position}</div>}
                                    {slide.company && <div className="company">{slide.company}</div>}
                                 </div>
                              )}

                              {slide.quote && <div dangerouslySetInnerHTML={{ __html: slide.quote }} suppressHydrationWarning className="quote" />}
                           </div>
                        )}
                     </div>
                  </div>
               )
            })}
         </div>

         <div className="grid-container">
            <div className="grid-row">
               <div className="c-12">
                  <div className="slide-component__progress-indicator">
                     <div className="base"></div>
                     <div
                        className="knob"
                        style={{
                           width: `${trackPercentage * 100}%`,
                           left: `${trackProgress * 100}%`,
                        }}
                     ></div>
                  </div>
               </div>
            </div>
         </div>
      </div>
   )
}
